import { h, replaceContent } from '../lib/dom.js';
import { money, pct } from '../lib/format.js';
import { calculateFeaso } from '../../shared/feaso.js';
import {
  TIER1_DEFAULTS,
  TIER2_DEFAULTS,
  subdivisionFee,
} from '../../shared/defaults.js';
import {
  getScenario,
  saveScenario,
  getDefaultOverrides,
} from '../lib/storage.js';
import { navigate } from '../lib/router.js';

const EMPTY = () => ({
  name: '',
  address: '',
  purchase_price: 780000,
  dwellings: 3,
  sale_price_per_dwelling: 820000,
  sale_price_contingency_pct: 0,
  holding_months: 12,
  dwelling_m2: TIER2_DEFAULTS.dwelling_m2,
  cost_per_m2: TIER2_DEFAULTS.cost_per_m2,
  overrides: {},
});

export function FeasoPage({ params, query }) {
  const libraryOverrides = getDefaultOverrides();
  let state;
  if (params.id) {
    const saved = getScenario(params.id);
    state = saved
      ? { ...saved.inputs, id: saved.id, name: saved.name }
      : EMPTY();
  } else {
    state = { ...EMPTY(), overrides: { ...libraryOverrides } };
  }
  for (const k of ['address', 'purchase_price', 'dwellings', 'sale_price_per_dwelling']) {
    const v = query[k];
    if (v == null || v === '') continue;
    if (k === 'address') {
      state[k] = v;
    } else {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) state[k] = n;
    }
  }

  const reportEl = h('section', { class: 'rise' });

  const recompute = () => {
    try {
      const r = calculateFeaso(toCalcInput(state));
      replaceContent(reportEl, ...report(r, state, onSave));
    } catch (e) {
      replaceContent(reportEl, errorPanel(e.message));
    }
  };

  const onChange = (patch) => { Object.assign(state, patch); recompute(); };
  const onOverride = (patch) => {
    state.overrides = { ...state.overrides };
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined) delete state.overrides[k];
      else state.overrides[k] = v;
    }
    recompute();
  };
  const onSave = () => {
    const outputs = calculateFeaso(toCalcInput(state));
    const saved = saveScenario({
      id: state.id,
      name: state.name || state.address || 'Untitled',
      inputs: { ...state },
      outputs: {
        forecast_profit: outputs.forecast_profit,
        profit_on_cost_pct: outputs.profit_on_cost_pct,
        net_sales: outputs.sales.net_sales,
        dev_costs: outputs.dev_costs,
      },
    });
    state.id = saved.id;
    navigate('/scenarios');
  };

  recompute();

  return h('div', { class: 'max-w-[1200px] mx-auto px-6 md:px-10' },
    // Folio header
    h('header', { class: 'pt-10 pb-6 border-b border-[color:var(--color-rule)] flex items-baseline justify-between gap-6 flex-wrap' },
      h('div', null,
        h('div', { class: 'folio' }, 'Feasibility · Single sheet'),
        h('h1', { class: 'display display-md mt-1' }, state.address || 'Untitled site'),
      ),
      h('div', { class: 'folio text-[color:var(--color-ink-dim)]' },
        `${state.dwellings} dwelling${state.dwellings === 1 ? '' : 's'} · ${state.holding_months} mo hold`,
      ),
    ),

    h('div', { class: 'grid grid-cols-1 lg:grid-cols-[22rem_minmax(0,1fr)] gap-10 md:gap-14 py-8' },
      form(state, onChange, onOverride),
      reportEl,
    ),
  );
}

function toCalcInput(s) {
  return {
    purchase_price: s.purchase_price,
    dwellings: s.dwellings,
    sale_price_per_dwelling: s.sale_price_per_dwelling,
    sale_price_contingency_pct: s.sale_price_contingency_pct,
    holding_months: s.holding_months,
    dwelling_m2: s.dwelling_m2,
    cost_per_m2: s.cost_per_m2,
    overrides: s.overrides,
  };
}

// ─── Form ────────────────────────────────────────────────────────────────

function form(state, onChange, onOverride) {
  return h('aside', { class: 'space-y-8' },
    formSection('The deal', [
      textField('Name or reference', state.name, (v) => onChange({ name: v }), 'e.g. 65 Bluehills'),
      textField('Address', state.address, (v) => onChange({ address: v })),
      twoUp(
        moneyField('Purchase', state.purchase_price, (v) => onChange({ purchase_price: v })),
        intField('Dwellings', state.dwellings, (v) => onChange({ dwellings: v }), { min: 1, max: 8 }),
      ),
      moneyField('Sale price / dwelling', state.sale_price_per_dwelling, (v) => onChange({ sale_price_per_dwelling: v })),
      twoUp(
        pctField('Sale contingency', state.sale_price_contingency_pct, (v) => onChange({ sale_price_contingency_pct: v })),
        intField('Hold (months)', state.holding_months, (v) => onChange({ holding_months: v }), { min: 3, max: 36 }),
      ),
    ]),

    disclosureSection('Build assumptions',
      twoUp(
        intField('m² / dwelling', state.dwelling_m2, (v) => onChange({ dwelling_m2: v }), { min: 50, step: 'any' }),
        moneyField('Rate $/m²', state.cost_per_m2, (v) => onChange({ cost_per_m2: v })),
      ),
      twoUp(
        moneyField('Demolition', state.overrides.demolition ?? TIER2_DEFAULTS.demolition, (v) => onOverride({ demolition: v })),
        moneyField('Design & approvals', state.overrides.design_approvals ?? TIER2_DEFAULTS.design_approvals, (v) => onOverride({ design_approvals: v })),
      ),
      twoUp(
        moneyField(
          `Subdivision (auto ${money(subdivisionFee(state.dwellings))})`,
          state.overrides.subdivision,
          (v) => onOverride({ subdivision: v || undefined }),
          'override',
        ),
        moneyField('Finance fees', state.overrides.finance_fees ?? TIER2_DEFAULTS.finance_fees, (v) => onOverride({ finance_fees: v })),
      ),
      twoUp(
        moneyField('Marketing', state.overrides.marketing ?? TIER2_DEFAULTS.marketing, (v) => onOverride({ marketing: v })),
        moneyField('Authorities', state.overrides.authorities ?? TIER2_DEFAULTS.authorities, (v) => onOverride({ authorities: v })),
      ),
    ),

    disclosureSection('Rates & GST',
      twoUp(
        pctField('Stamp duty', resolve(state, 'stamp_duty_pct', TIER1_DEFAULTS), (v) => onOverride({ stamp_duty_pct: v })),
        pctField('Selling cost', resolve(state, 'selling_cost_pct', TIER1_DEFAULTS), (v) => onOverride({ selling_cost_pct: v })),
      ),
      twoUp(
        pctField('Land loan rate', resolve(state, 'land_loan_rate', TIER1_DEFAULTS), (v) => onOverride({ land_loan_rate: v })),
        pctField('Dev loan rate', resolve(state, 'dev_loan_rate', TIER1_DEFAULTS), (v) => onOverride({ dev_loan_rate: v })),
      ),
      twoUp(
        pctField('Build contingency', resolve(state, 'construction_contingency_pct', TIER1_DEFAULTS), (v) => onOverride({ construction_contingency_pct: v })),
        checkboxField('GST margin scheme',
          state.overrides.gst_margin_scheme ?? TIER1_DEFAULTS.gst_margin_scheme,
          (v) => onOverride({ gst_margin_scheme: v }),
        ),
      ),
    ),
  );
}

function resolve(state, key, defaults) {
  return state.overrides[key] ?? defaults[key];
}

function formSection(title, fields) {
  return h('section', null,
    h('div', { class: 'folio mb-4' }, title),
    h('div', { class: 'space-y-4' }, ...fields),
  );
}

function disclosureSection(title, ...fields) {
  const panel = h('div', { class: 'space-y-4 pt-4' }, ...fields);
  return h('details', { class: 'group border-t border-[color:var(--color-rule)] pt-5' },
    h('summary', { class: 'flex items-center justify-between folio hover:text-[color:var(--color-ink)]' },
      h('span', null, title),
      h('span', { class: 'text-[color:var(--color-ink-faint)] group-open:rotate-45 transition-transform' }, '+'),
    ),
    panel,
  );
}

function twoUp(a, b) {
  return h('div', { class: 'grid grid-cols-2 gap-4' }, a, b);
}

// Field primitives

function textField(label, value, onInput, placeholder) {
  return h('label', { class: 'block' },
    h('span', { class: 'field-label' }, label),
    h('input', {
      type: 'text',
      class: 'field',
      value: value ?? '',
      placeholder: placeholder ?? '',
      oninput: (e) => onInput(e.target.value),
    }),
  );
}

function moneyField(label, value, onInput, placeholder) {
  return h('label', { class: 'block' },
    h('span', { class: 'field-label' }, label),
    h('input', {
      type: 'text',
      inputmode: 'numeric',
      class: 'field',
      value: value == null ? '' : formatEditable(value),
      placeholder: placeholder ?? '',
      oninput: (e) => {
        const raw = e.target.value.replace(/[^\d.-]/g, '');
        const n = raw === '' ? undefined : Number(raw);
        onInput(Number.isFinite(n) ? n : undefined);
      },
      onblur: (e) => {
        const raw = e.target.value.replace(/[^\d.-]/g, '');
        if (raw !== '') e.target.value = formatEditable(Number(raw));
      },
      onfocus: (e) => e.target.select(),
    }),
  );
}

function intField(label, value, onInput, opts = {}) {
  return h('label', { class: 'block' },
    h('span', { class: 'field-label' }, label),
    h('input', {
      type: 'number',
      class: 'field',
      value: value ?? '',
      min: opts.min,
      max: opts.max,
      step: opts.step ?? 1,
      oninput: (e) => {
        const v = e.target.value === '' ? undefined : Number(e.target.value);
        onInput(Number.isFinite(v) ? v : undefined);
      },
    }),
  );
}

function pctField(label, value, onInput) {
  return h('label', { class: 'block' },
    h('span', { class: 'field-label' }, label),
    h('div', { class: 'relative' },
      h('input', {
        type: 'number',
        class: 'field pr-6',
        value: value == null ? '' : +(value * 100).toFixed(4),
        step: 0.05,
        min: 0,
        oninput: (e) => {
          const v = e.target.value === '' ? undefined : Number(e.target.value) / 100;
          onInput(Number.isFinite(v) ? v : undefined);
        },
      }),
      h('span', { class: 'absolute right-0 bottom-2 text-[color:var(--color-ink-faint)] text-sm' }, '%'),
    ),
  );
}

function checkboxField(label, value, onChange) {
  return h('label', { class: 'flex items-end justify-between gap-3 pb-1 border-b border-[color:var(--color-rule)]' },
    h('span', { class: 'field-label mb-0' }, label),
    h('input', {
      type: 'checkbox',
      checked: !!value,
      class: 'h-4 w-4 accent-[color:var(--color-olive-700)]',
      onchange: (e) => onChange(e.target.checked),
    }),
  );
}

function formatEditable(n) {
  return new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 }).format(n);
}

// ─── Report ──────────────────────────────────────────────────────────────

function report(r, state, onSave) {
  const poc = r.profit_on_cost_pct;
  const signal =
    poc >= 0.15 ? 'text-[color:var(--color-positive)]'
    : poc >= 0.10 ? 'text-[color:var(--color-warning)]'
    : 'text-[color:var(--color-negative)]';
  const verdict =
    poc >= 0.15 ? 'Viable — above 15%.'
    : poc >= 0.10 ? 'Marginal — 10–15% on cost.'
    : 'Thin — below 10% on cost.';

  return [
    // Headline
    h('div', { class: 'grid grid-cols-1 md:grid-cols-12 gap-6 items-end pb-8 border-b border-[color:var(--color-rule)]' },
      h('div', { class: 'md:col-span-7' },
        h('div', { class: 'folio' }, 'Forecast profit'),
        h('div', { class: `display display-lg num mt-2 ${signal}` }, money(r.forecast_profit)),
        h('div', { class: 'mt-2 text-[color:var(--color-ink-soft)]' },
          `on ${money(r.total_cost_basis)} total cost · ${verdict}`,
        ),
      ),
      h('div', { class: 'md:col-span-5 md:text-right' },
        h('div', { class: 'folio' }, 'Profit on cost'),
        h('div', { class: `display display-lg num mt-2 ${signal}` }, pct(poc)),
        h('div', { class: 'mt-2 text-[color:var(--color-ink-soft)] num' },
          `${money(Math.round(r.forecast_profit / state.dwellings))} per dwelling`,
        ),
      ),
    ),

    // Sales
    reportBlock('Sale proceeds', [
      reportRow('Gross sales', money(r.sales.gross_sales), { note: `${state.dwellings} × ${money(state.sale_price_per_dwelling)}` }),
      r.sales.sale_contingency_reduction > 0
        ? reportRow('Sale contingency', `− ${money(r.sales.sale_contingency_reduction)}`, { muted: true })
        : null,
      reportRow(`Selling costs · ${formatPct(resolve(state, 'selling_cost_pct', TIER1_DEFAULTS))}`, `− ${money(r.sales.selling_costs)}`, { muted: true }),
      reportRow('Net sales', money(r.sales.net_sales), { strong: true }),
    ]),

    // Site
    reportBlock('Site acquisition', [
      reportRow('Purchase price', money(r.site_acquisition.purchase_price)),
      reportRow(`Stamp duty · ${formatPct(resolve(state, 'stamp_duty_pct', TIER1_DEFAULTS))}`, money(r.site_acquisition.stamp_duty), { muted: true }),
      reportRow('Total site', money(r.site_acquisition.total), { strong: true }),
    ]),

    // Dev costs
    reportBlock('Development costs', [
      reportRow('Design & approvals', money(r.dev_cost_breakdown.design_approvals)),
      reportRow('Subdivision', money(r.dev_cost_breakdown.subdivision)),
      r.dev_cost_breakdown.marketing > 0 ? reportRow('Marketing', money(r.dev_cost_breakdown.marketing)) : null,
      r.dev_cost_breakdown.authorities > 0 ? reportRow('Authorities', money(r.dev_cost_breakdown.authorities)) : null,
      reportRow('Finance fees', money(r.dev_cost_breakdown.finance_fees)),
      reportRow('Interest', money(r.dev_cost_breakdown.interest), {
        note: `land ${money(r.interest_breakdown.land)} · dev ${money(r.interest_breakdown.development)}`,
      }),
      reportRow('Demolition', money(r.dev_cost_breakdown.demolition)),
      reportRow(
        'Construction',
        money(r.dev_cost_breakdown.construction),
        { note: `${state.dwellings} × ${state.dwelling_m2}m² × ${money(state.cost_per_m2)}` },
      ),
      reportRow(`Contingency · ${formatPct(resolve(state, 'construction_contingency_pct', TIER1_DEFAULTS))}`, money(r.dev_cost_breakdown.construction_contingency), { muted: true }),
      reportRow('Total development', money(r.dev_costs), { strong: true }),
    ]),

    // GST
    reportBlock('GST', [
      reportRow('GST liability', `− ${money(r.gst.liability)}`, { muted: true, note: '(sales − non-interest dev) ÷ 11' }),
      reportRow('Margin scheme credit', money(r.gst.margin_credit), { muted: true, note: 'purchase ÷ 11' }),
      reportRow('Net GST payable', `− ${money(r.gst.net)}`, { strong: true }),
    ]),

    // Actions
    h('div', { class: 'pt-6 flex flex-wrap items-center gap-3 border-t border-[color:var(--color-rule)]' },
      h('button', { type: 'button', class: 'btn btn-ink', onclick: onSave },
        state.id ? 'Update scenario' : 'Save to ledger',
      ),
      h('button', { type: 'button', class: 'btn btn-ghost', onclick: () => copyShareLink(state) },
        'Copy shareable link',
      ),
      h('span', { class: 'folio ml-auto text-[color:var(--color-ink-dim)]' },
        'Prepared ', new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }),
      ),
    ),
  ];
}

function reportBlock(title, rows) {
  const filtered = rows.filter(Boolean);
  return h('section', { class: 'py-6 border-b border-[color:var(--color-rule)]' },
    h('div', { class: 'flex items-baseline justify-between mb-4' },
      h('h3', { class: 'display display-sm' }, title),
      h('span', { class: 'folio text-[color:var(--color-ink-faint)]' },
        String(rows.filter(Boolean).length).padStart(2, '0'), ' items',
      ),
    ),
    h('ul', { class: 'space-y-1' }, ...filtered),
  );
}

function reportRow(label, value, opts = {}) {
  const { strong, muted, note } = opts;
  return h('li', {
    class: [
      'leader py-1.5',
      strong ? 'font-medium pt-3 mt-2 border-t border-[color:var(--color-rule-soft)]' : '',
      muted && !strong ? 'text-[color:var(--color-ink-soft)]' : '',
    ].join(' '),
  },
    h('span', { class: 'min-w-0' },
      h('span', null, label),
      note ? h('span', { class: 'block text-[0.72rem] text-[color:var(--color-ink-faint)] italic' }, note) : null,
    ),
    h('span', { class: 'dots' }),
    h('span', {
      class: `num shrink-0 ${strong ? 'display display-sm' : ''}`,
    }, value),
  );
}

function errorPanel(msg) {
  return h('div', { class: 'p-6 border border-[color:var(--color-negative)] text-[color:var(--color-negative)]' },
    h('div', { class: 'folio mb-1' }, 'Cannot compute'),
    h('div', null, msg),
  );
}

function formatPct(v) { return `${(v * 100).toFixed(2)}%`; }

function copyShareLink(state) {
  const params = new URLSearchParams({
    address: state.address || '',
    purchase_price: state.purchase_price,
    dwellings: state.dwellings,
    sale_price_per_dwelling: state.sale_price_per_dwelling,
  });
  const url = `${window.location.origin}${window.location.pathname}#/feaso?${params}`;
  navigator.clipboard?.writeText(url);
}
